// Карточка «Истории просмотра» (макет «Профиль», кадр История просмотра):
// фото 149×197 r4 с сердцем (белая заливка 50 % + чёрная обводка 50 %),
// цена #FA5C2E 14/700 + зачёркнутая старая #ABABAB 12 + бейдж скидки r6,
// название 14, серая кнопка «В корзину» h32 r8 с отступом 6 от краёв.
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { toast } from "sonner";
import type { Product } from "@/api";
import { FavoriteHeartIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { useFormatPrice } from "@/lib/use-format-price";
import { useCart, useWishlist } from "@/store";
import { orderQty } from "@/lib/min-order";
export type HistoryItem = {
  productId: string;
  name: string;
  image?: string | null;
  price: number;
  oldPrice?: number;
  product?: Product;
};
export function HistoryCard({
  item
}: {
  item: HistoryItem;
}) {
  const fmt = useFormatPrice();
  const navigate = useNavigate();
  const add = useCart(s => s.add);
  const liked = useWishlist(s_0 => s_0.ids.includes(item.productId));
  const toggleProduct = useWishlist(s_1 => s_1.toggleProduct);
  const setProductLikedOnServer = useWishlist(s_2 => s_2.setProductLikedOnServer);
  const [busy, setBusy] = useState(false);
  const discount = item.oldPrice && item.oldPrice > item.price ? Math.round((1 - item.price / item.oldPrice) * 100) : 0;

  // В корзину сразу — только когда выбирать нечего (одна вариация/размер);
  // иначе ведём на карточку товара выбрать размер.
  const toCart = async () => {
    const p = item.product;
    if (!p || p.status !== "published" || p.stock <= 0) {
      navigate(`/product/${item.productId}`);
      return;
    }
    const variants = p.variants?.filter(v => v.stock > 0) ?? [];
    if (variants.length > 1 || !variants.length && p.sizes.length > 1) {
      navigate(`/product/${item.productId}`);
      return;
    }
    setBusy(true);
    try {
      const v_0 = variants[0];
      await add({
        productId: p.id,
        variantId: v_0?.id,
        size: v_0?.size ?? p.sizes[0],
        color: v_0?.color ?? p.color,
        qty: orderQty(p)
      });
      toast.success("Товар в корзине");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Не удалось добавить в корзину");
    } finally {
      setBusy(false);
    }
  };
  return <article className="flex min-w-0 flex-col">
			<div className="relative">
				<Link to={`/product/${item.productId}`} className="block aspect-[149/197] overflow-hidden rounded-[4px] bg-[#EDEDED] outline-none focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40">
					{item.image ? <img src={item.image} alt={item.name} loading="lazy" className="size-full object-cover" /> : null}
				</Link>
				<button type="button" aria-label={liked ? "Убрать из избранного" : "В избранное"} aria-pressed={liked} onClick={() => {
        const next = toggleProduct(item.productId);
        void setProductLikedOnServer(item.productId, next);
      }} className="absolute top-2 right-2 grid size-8 place-items-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-white">
					<FavoriteHeartIcon active={liked} />
				</button>
			</div>
			<div className="mt-2 flex min-w-0 items-center gap-1 px-[6px] whitespace-nowrap">
				<span className="text-[16px] leading-5 font-semibold text-[#FA5C2E]">{fmt(item.price)}</span>
				{discount > 0 && item.oldPrice ? <>
						<span className="truncate text-[12px] leading-3 font-semibold text-[#ABABAB] line-through">{fmt(item.oldPrice)}</span>
						<span className="shrink-0 rounded-[6px] bg-[#FA5C2E] px-[3px] pt-px pb-0.5 text-[10px] leading-[10px] font-semibold tracking-[-0.3px] text-white">
							−{discount}%
						</span>
					</> : null}
			</div>
			<Link to={`/product/${item.productId}`} className="mt-2 truncate px-[6px] text-[14px] leading-[14px] tracking-[-0.42px] text-[#1F1F1F] outline-none hover:text-[#FA5C2E] focus-visible:underline">
				{item.name}
			</Link>
			<Button variant="gray" size="sm" disabled={busy} onClick={() => void toCart()} className="mx-[6px] mt-2">
				В корзину
			</Button>
		</article>;
}